import { capitalize } from 'lodash';

import {
  type DraftTov,
  LANGUAGE,
  type Language,
  type ToneOfVoice,
  type TovConfiguration,
  type TovInput,
  type TovTrait,
} from 'src/data/generation';
import {
  type GenerationTool,
  isBasicContentImproverTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import type { Workspace } from 'src/data/workspace';
import type { EntityWithName, ID } from 'src/lib/collection';

import {
  DEFAULT_SUGGEST_TOV_MODE,
  DRAFT_TOV_ID,
  PRESET_TONE_OF_VOICE,
  PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS,
  PRESET_TONE_OF_VOICE_PROPS,
  type PresetToneOfVoice,
  type SelectedTov,
} from './const';
import { getNonEnglishTraitButNot, getTraitLabel } from './traits';
import type { CustomToneOfVoice } from './types';

export const isPresetTov = (tov: string): tov is PresetToneOfVoice =>
  Object.values(PRESET_TONE_OF_VOICE).includes(tov as PresetToneOfVoice);

export const getToneOfVoiceTitle = (tov: PresetToneOfVoice, language?: Language): string => {
  if (language && PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language]?.[tov]) {
    return PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language][tov].title;
  }
  return PRESET_TONE_OF_VOICE_PROPS[tov]?.title || '';
};
export const getToneOfVoiceDescription = (tov: PresetToneOfVoice, language?: Language): string => {
  if (language && PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language]?.[tov]) {
    return PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language][tov].description;
  }
  return PRESET_TONE_OF_VOICE_PROPS[tov]?.description || '';
};
export const getToneOfVoiceLabel = (tov: PresetToneOfVoice, language?: Language): string => {
  if (language && PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language]?.[tov]) {
    return PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language][tov].label || '';
  }
  return PRESET_TONE_OF_VOICE_PROPS[tov]?.label || '';
};

const BUT_NOT_MAP: Record<string, string> = {
  [LANGUAGE.de]: 'aber nicht',
  [LANGUAGE.de_in]: 'aber nicht',
  [LANGUAGE.de_ch]: 'aber nicht',
  [LANGUAGE.el]: 'αλλά όχι',
  [LANGUAGE.es]: 'pero no',
  [LANGUAGE.es_la]: 'pero no',
  [LANGUAGE.es_cs]: 'pero no',
  [LANGUAGE.tr]: 'ama değil',
  [LANGUAGE.fr]: 'mais pas',
  [LANGUAGE.fr_qu]: 'mais pas',
  [LANGUAGE.bg]: 'но не',
  [LANGUAGE.cs]: 'ale ne',
  [LANGUAGE.da]: 'men ikke',
  [LANGUAGE.nl]: 'maar niet',
  [LANGUAGE.nl_be]: 'maar niet',
  [LANGUAGE.hu]: 'de nem',
  [LANGUAGE.it]: 'ma non',
  [LANGUAGE.pl]: 'ale nie',
  [LANGUAGE.pt_pt]: 'mas não',
  [LANGUAGE.pt_br]: 'mas não',
  [LANGUAGE.ro]: 'dar nu',
  [LANGUAGE.ru]: 'но не',
  [LANGUAGE.cmn]: '但不',
  [LANGUAGE.yue]: '但不',
  [LANGUAGE.zh_tw]: '但不',
  [LANGUAGE.zh_cn]: '但不',
  [LANGUAGE.th]: 'แต่ไม่',
  [LANGUAGE.ms]: 'tetapi tidak',
  [LANGUAGE.sv]: 'men inte',
  [LANGUAGE.ko]: '하지만 아니오',
};
const getButNotText = (language?: Language) => (language && BUT_NOT_MAP[language]) || 'but not';

export const getTovTexts = (tov: SelectedTov, toneOfVoices: ToneOfVoice[], language?: Language) => {
  const presetTov = tov as PresetToneOfVoice;
  if (isPresetTov(presetTov)) {
    return {
      tovTitle: getToneOfVoiceTitle(presetTov, language),
      tovDescription: getToneOfVoiceDescription(presetTov, language),
      tovLabel: getToneOfVoiceLabel(presetTov, language),
    };
  }

  const toneOfVoice = toneOfVoices.find((toneOfVoice) => toneOfVoice.id === tov);
  return {
    tovTitle: toneOfVoice?.name,
    tovDescription: toneOfVoice?.description,
    tovLabel: toneOfVoice?.brandVoiceName,
  };
};

export const getToneOfVoiceForGeneration = (
  selectedTov?: SelectedTov,
  draftTov?: DraftTov
): TovInput => {
  if (draftTov && selectedTov === DRAFT_TOV_ID) {
    return {
      draftTov: {
        personality: draftTov.personality,
        traits: draftTov.traits,
        description: draftTov.description,
      },
    };
  }

  const presetTov = selectedTov as PresetToneOfVoice;
  if (isPresetTov(presetTov)) {
    return { defaultPreset: presetTov };
  } else {
    return { customId: selectedTov };
  }
};

export const getTovInputFromConfiguration = (config?: TovConfiguration): TovInput | undefined => {
  if (!config) {
    return undefined;
  }

  return getToneOfVoiceForGeneration(config.customModelId || config.tov, config.draftToneOfVoice);
};

export const getToneOfVoiceForInfinity = (
  selectedTov: SelectedTov,
  toneOfVoices: ToneOfVoice[]
): string | undefined => {
  if (!selectedTov) {
    return;
  }

  const { tovTitle } = getTovTexts(selectedTov, toneOfVoices);
  return tovTitle;
};

export const getToneOfVoiceFromGeneration = ({
  tovInput,
  generationCustomModelId,
  draftToneOfVoice,
}: {
  tovInput: TovInput;
  generationCustomModelId: ID;
  draftToneOfVoice: DraftTov;
}) => {
  if (generationCustomModelId) {
    return {
      selectedCustomModelId: generationCustomModelId,
      selectedTov: null,
      draftToneOfVoice: null,
    };
  }
  if (draftToneOfVoice) {
    return {
      selectedCustomModelId: null,
      selectedTov: DRAFT_TOV_ID,
      draftToneOfVoice: { ...draftToneOfVoice, name: '' },
    };
  }
  if (tovInput?.draftTov) {
    return {
      selectedCustomModelId: null,
      selectedTov: DRAFT_TOV_ID,
      draftToneOfVoice: { ...tovInput.draftTov, name: '' },
    };
  }
  return {
    selectedCustomModelId: null,
    selectedTov: tovInput?.customId || tovInput?.defaultPreset || DEFAULT_SUGGEST_TOV_MODE,
    draftToneOfVoice: null,
  };
};

export const getToneOfVoiceForTrackingEvent = (
  tovInput: TovInput,
  toneOfVoices: ToneOfVoice[]
): { tov: string; tovDescription?: string } => {
  if (tovInput?.draftTov) {
    return {
      tovDescription: formatToneOfVoiceDescription(tovInput?.draftTov),
      tov: 'draft_tov',
    };
  }
  const customToneOfVoice = toneOfVoices.find(
    (toneOfVoice) => toneOfVoice.id === tovInput?.customId
  );
  if (customToneOfVoice) {
    return {
      tov: 'custom_tov',
      tovDescription: formatToneOfVoiceDescription(customToneOfVoice),
    };
  }
  return {
    tov: tovInput?.defaultPreset || DEFAULT_SUGGEST_TOV_MODE,
  };
};

export const formatToneOfVoiceTrait = (data: TovTrait, language?: Language) => {
  const traitLabel = getTraitLabel(data.trait, language);
  if (!data.butNot) {
    return capitalize(traitLabel);
  }
  return `${capitalize(traitLabel)} ${getButNotText(language)} ${getNonEnglishTraitButNot(
    data.butNot,
    language,
    data.trait
  )}`;
};

export const formatToneOfVoiceDescription = (
  tov: DraftTov | CustomToneOfVoice,
  options?: { language?: Language; multiline?: boolean }
) => {
  const result = [];
  if (tov.traits?.length) {
    result.push(
      `${tov.traits
        .map((trait) => `${formatToneOfVoiceTrait(trait, options?.language)}`)
        .join(', ')}`
    );
  }
  if (tov.personality) {
    result.push(
      `Personality: ${capitalize(getToneOfVoiceDescription(tov.personality, options?.language))}`
    );
  }
  if (tov.description) {
    result.push(`Custom instructions: ${tov.description}`);
  }
  return result.join(options?.multiline ? '\n' : ', ');
};

export const isEmptyTovData = (data: DraftTov | CustomToneOfVoice) =>
  (!data.traits || data.traits?.length === 0) && !data.personality && !data.description;

export const getWorkspaceDefaultToneOfVoice = (
  workspace: Workspace,
  generationTool: GenerationTool
): EntityWithName | undefined => {
  const workspaceDefaultToneOfVoice = (workspace?.defaultTovAnalyzerForTool || [])?.find(
    (item) => item.generationTool === generationTool
  );
  if (!workspaceDefaultToneOfVoice) {
    return undefined;
  }

  return {
    id: workspaceDefaultToneOfVoice.customTovId,
    name: workspaceDefaultToneOfVoice.customTovName,
  };
};

export const getPresetTonesOfVoice = (generationTool: GenerationTool): PresetToneOfVoice[] => {
  const presets = Object.values(PRESET_TONE_OF_VOICE);
  if (isRewriteGenerationTool(generationTool) || isBasicContentImproverTool(generationTool)) {
    return presets.filter((tov) => tov !== PRESET_TONE_OF_VOICE.engaging);
  }
  return presets;
};
